/**
 * Screenshot capture using html2canvas (lazy-loaded).
 * Returns a JPEG Blob.
 */

let html2canvasModule: typeof import("html2canvas") | null = null;

/**
 * Capture the full page as a JPEG blob.
 * Hides the feedback widget during capture.
 *
 * @param widgetRoot - The widget's root element to hide during capture
 * @returns JPEG Blob
 */
export async function captureScreenshot(widgetRoot: HTMLElement | null): Promise<Blob | null> {
  try {
    // Lazy-load html2canvas
    if (!html2canvasModule) {
      html2canvasModule = await import("html2canvas");
    }

    const html2canvas = html2canvasModule.default;

    // Hide widget during capture
    if (widgetRoot) {
      widgetRoot.style.display = "none";
    }

    try {
      const canvas = await html2canvas(document.documentElement, {
        useCORS: true,
        logging: false,
        scale: 1,
        ignoreElements: (el: Element) => {
          const tag = el.tagName;
          if (tag === "LINK" || tag === "STYLE" || tag === "SCRIPT") {
            const src =
              (el as HTMLLinkElement).href || (el as HTMLScriptElement).src || el.textContent || "";
            if (
              src.includes("chrome-extension://") ||
              src.includes("moz-extension://") ||
              src.includes("safari-web-extension://")
            )
              return true;
          }
          return (
            el.classList?.contains?.("ip-overlay") || el.classList?.contains?.("ip-modal") || false
          );
        },
      });

      return await new Promise<Blob | null>((resolve) => {
        canvas.toBlob((blob) => resolve(blob), "image/jpeg", 0.7);
      });
    } finally {
      if (widgetRoot) {
        widgetRoot.style.display = "";
      }
    }
  } catch (err) {
    console.warn("IPFeedback: Screenshot capture failed:", err);
    return null;
  }
}
